import { useLingui } from '@lingui/react/macro'
import type { MenuProps } from 'antd'
import { Dropdown } from 'antd'
import { ArrowRight, ExternalLink, Plus } from 'lucide-react'
import React, { useCallback, useMemo, useState } from 'react'
import styled, { css, keyframes } from 'styled-components'

const FADE_OUT_DURATION_MS = 1500

const fadeOut = keyframes`
  from {
    opacity: 1;
  }
  to {
    opacity: 0;
  }
`

const TriggerAnchor = styled.div<{ $x: number; $y: number; $fading: boolean }>`
  position: fixed;
  left: ${(props) => props.$x}px;
  top: ${(props) => props.$y}px;
  width: 1px;
  height: 1px;
  pointer-events: none;

  ${({ $fading }) =>
    $fading &&
    css`
      & + .ant-dropdown {
        animation: ${fadeOut} ${FADE_OUT_DURATION_MS}ms ease-out forwards;
      }
    `}
`

const CompactMenu = styled.div`
  .ant-dropdown-menu {
    padding: 0.25rem;
  }

  .ant-dropdown-menu .ant-dropdown-menu-item {
    padding: 0.25rem 0.5rem;
    font-size: 0.75rem;
    line-height: 1.4;
  }
`

const MenuLabel = styled.span`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`

const MenuShortcut = styled.span`
  opacity: 0.6;
  font-size: 0.625rem;
`

export type ContextMenuProps = {
  position: { x: number; y: number } | null
  onClose: () => void
  onOpen?: () => void
  onOpenInNewTab?: () => void
  onCrossReference?: () => void
  onAddToContext?: () => void
  showNavigationOptions?: boolean
  showAddToContext?: boolean
}

export const ContextMenu: React.FC<ContextMenuProps> = ({
  position,
  onClose,
  onOpen,
  onOpenInNewTab,
  onAddToContext,
  onCrossReference,
  showNavigationOptions = true,
  showAddToContext = false,
}) => {
  const { t } = useLingui()
  const [isFading, setIsFading] = useState(false)

  const handleOpen = useCallback(() => {
    onOpen?.()
    onClose()
  }, [onOpen, onClose])

  const handleOpenInNewTab = useCallback(() => {
    onOpenInNewTab?.()
    onClose()
  }, [onOpenInNewTab, onClose])

  const handleAddToContext = useCallback(() => {
    onAddToContext?.()
    setIsFading(true)
    setTimeout(() => {
      setIsFading(false)
      onClose()
    }, FADE_OUT_DURATION_MS)
  }, [onAddToContext, onClose])

  const menuItems: MenuProps['items'] = useMemo(() => {
    const items: MenuProps['items'] = []
    if (onCrossReference)
      items.push({
        key: 'cross-reference',
        label: t`Cross-reference`,
        onClick: () => {
          onCrossReference()
          onClose()
        },
      })

    if (showNavigationOptions && onOpen) {
      items.push({
        key: 'open',
        icon: <ArrowRight size={14} />,
        label: t`Open`,
        onClick: handleOpen,
      })
    }

    if (showNavigationOptions && onOpenInNewTab) {
      items.push({
        key: 'open-new-tab',
        icon: <ExternalLink size={14} />,
        label: (
          <MenuLabel>
            {t`Open in New Tab`}
            <MenuShortcut>{t`⌘+Click`}</MenuShortcut>
          </MenuLabel>
        ),
        onClick: handleOpenInNewTab,
      })
    }

    if (showAddToContext && onAddToContext) {
      if (items.length > 0) {
        items.push({ type: 'divider' })
      }
      items.push({
        key: 'add-to-context',
        icon: <Plus size={14} />,
        label: t`Add to context`,
        onClick: handleAddToContext,
      })
    }

    return items
  }, [
    t,
    onCrossReference,
    onClose,
    showNavigationOptions,
    showAddToContext,
    onOpen,
    onOpenInNewTab,
    onAddToContext,
    handleOpen,
    handleOpenInNewTab,
    handleAddToContext,
  ])

  if (!position || menuItems.length === 0) {
    return null
  }

  return (
    <Dropdown
      menu={{ items: menuItems }}
      popupRender={(menu) => <CompactMenu>{menu}</CompactMenu>}
      open={true}
      onOpenChange={(open) => {
        if (!open && !isFading) {
          onClose()
        }
      }}
      trigger={['click']}>
      <TriggerAnchor $x={position.x} $y={position.y} $fading={isFading} />
    </Dropdown>
  )
}
